import React from 'react';
import {
  Row,
  Col,
  Typography,
  Tooltip,
  Popconfirm
} from 'antd';
import {
  Edit,
  Copy,
  Trash2,
  Star,
  HelpCircle,
  ChevronDown,
    FastForward
} from 'lucide-react';
import Button from '@mui/material/Button';
import ButtonGroup from '@mui/material/ButtonGroup';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { removePortfolioPath } from '../../api/pathApi';
import { setSelectedPath } from '../../store/slices/path.slice';
import useCompany from '../Company/useCompany';
import { selectFullPortfolios } from '../../store/slices/portfolios.slice';
import useClipboard from '../../hooks/useClipboard';
const { Title, Text } = Typography;

const PathList = ({ paths, onEdit }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const copyClipboard = useClipboard();
  const { allowAdminActions, updateCompanyPortfolioPath, portfolioPathId } = useCompany();

  // Gather all Portfolio IDs to retrieve from the store
  const uniquePathPortfolioIds = paths.map((p) => p.portfolio_ids).flat();
  const portfolios = useSelector((state) => selectFullPortfolios(state, uniquePathPortfolioIds));

  // If you have a question store or selector for retrieving question data:
  // const uniquePathQuestionIds = paths.map((p) => p.question_ids).flat();
  // const questions = useSelector((state) => selectFullQuestions(state, uniquePathQuestionIds));

  const handleUsePath = (path) => {
    dispatch(
      setSelectedPath({
        pathId: path.id,
        portfolioIds: path.portfolio_ids,
        questionIds: path.question_ids
      })
    );
    navigate(`/portfolios/paths/${path.id}/use`);
  };

  const handleSetCompanyPath = (pathId) => {
    updateCompanyPortfolioPath(pathId);
  };

  const handleRemovePath = (pathId) => {
    dispatch(removePortfolioPath({ pathId }))
      .unwrap()
      .catch((err) => console.error('Failed to remove path:', err));
  };

  const getPortfolioNames = (path) => (
    <div>
      {path.portfolio_ids.map((pid) => {
        const p = portfolios.find((pf) => pf.id === pid);
        return <div key={pid}>{p?.name ?? 'Unknown Portfolio'}</div>;
      })}
    </div>
  );

  const getQuestionNames = (path) => (
    <div>
      {path.question_ids.map((qid) => (
        // If you have a "questions" array or store, you can look up the name:
        // const q = questions.find((qItem) => qItem.id === qid);
        // return <div key={qid}>{q?.title ?? `Question ${qid}`}</div>;
        <div key={qid}>Question {qid}</div>
      ))}
    </div>
  );

  return (
    <div style={{ width: '100%' }} className="mx-auto w-full">
      {paths.map((path) => (
        <Row
          key={path.id}
          justify="space-between"
          align="middle"
          style={{
            padding: '12px 0',
            borderBottom: '1px solid #F0F0F0'
          }}
        >
          {/* Left Section: Title */}
          <Col flex="0 0 200px">
            <Title level={5} style={{ marginBottom: 2, fontWeight: 500 }}>
              {path.title}
            </Title>
          </Col>

          <Col flex="none">
            <Row gutter={8} align="middle">
              <Col>
                <Tooltip title="Use Path">
                  <Button
                    type="text"
                    onClick={() => handleUsePath(path)}
                    endIcon={<FastForward size={16} color="#444" />}
                  >
                    <span className={'font-semibold'}>Use Path</span>
                  </Button>
                </Tooltip>
              </Col>

                <Col>
                  <ButtonGroup>
                    {path.uid === localStorage.getItem('uid') && (
                      <Tooltip title="Edit Path">
                        <Button variant="text" onClick={() => onEdit(path)}>
                          <Edit size={16} color="#444" />
                        </Button>
                      </Tooltip>
                    )}
                    <Tooltip title="Copy Path ID">
                      <Button variant="text" onClick={() => copyClipboard(path.id)}>
                        <Copy size={16} color="#444" />
                      </Button>
                    </Tooltip>
                    <Popconfirm
                      title="Are you sure you want to delete this path?"
                      onConfirm={() => handleRemovePath(path.id)}
                      okText="Yes"
                      cancelText="No"
                      placement="left"
                    >
                      <Tooltip title="Remove Path">
                        <Button variant="text">
                          <Trash2 size={16} color="#444" />
                        </Button>
                      </Tooltip>
                    </Popconfirm>
                  </ButtonGroup>
                </Col>
            </Row>
          </Col>

          {allowAdminActions && (
            <Col flex="0 0 160px">
              {/* Simple example: if it’s the company path, show a highlighted tag; otherwise, show “Set as Path” */}
              {portfolioPathId === path.id ? (
                <div className="inline-block px-2 py-1 rounded-lg bg-sky-300 text-white">
                  Company Path
                </div>
              ) : (
                <Button
                  type="text"
                  onClick={() => handleSetCompanyPath(path.id)}
                  style={{ color: '#1d4ed8' }}
                >
                  Set as Path
                </Button>
              )}
            </Col>
          )}
          <Col flex="0 0 400px">
            <Row
              justify="start"
              align="middle"
              gutter={16}
              style={{ display: 'flex', flexWrap: 'nowrap' }}
            >
              <Col>
                <Tooltip placement="top" title={getPortfolioNames(path)}>
                  <div className="flex items-center gap-2 px-2 py-1 rounded cursor-pointer">
                    <ChevronDown size={16} className="text-slate-700" />
                    <Text style={{ marginBottom: 0, color: '#333' }}>
                      {path.portfolio_ids.length} Portfolios
                    </Text>
                  </div>
                </Tooltip>
              </Col>

              <Col>
                <Tooltip placement="top" title={getQuestionNames(path)}>
                  <div className="flex items-center gap-2 px-2 py-1 rounded cursor-pointer">
                    <HelpCircle size={16} className="text-slate-700" />
                    <Text style={{ marginBottom: 0, color: '#333' }}>
                      {path.question_ids.length} Questions
                    </Text>
                  </div>
                </Tooltip>
              </Col>
            </Row>
          </Col>

        </Row>
      ))}
    </div>
  );
};

export default PathList;
